import { useCallback } from 'react';
import { flowResult } from 'mobx';
import { toastify } from '@shared/utils/toastify';
import { useStore } from './useStore';
import { CreditCard } from 'lucide-react';
import Heading from '../components/common/Heading';

/**
 * Custom hook for handling re-payment functionality
 * @returns Function to handle re-payment with confirmation modal
 */
export const useRepayment = () => {
    const { orderStore, modalStore } = useStore();

    const handleRepayment = useCallback((orderId: string | number) => {
        if (!orderId) {
            toastify('error', '注文情報がありません');
            return;
        }

        modalStore.showAlertModal({
            id: 'repayment-confirm',
            type: 'default',
            size: 'xl',
            title: '再支払確認',
            showHeader: false,
            content: <ConfimRepaymentContentModal />,
            cancelButtonClassName: 'md:min-h-12 md:min-w-[180px]',
            saveButtonClassName: 'md:min-h-12 md:min-w-[180px]',
            footerClassName: 'sm:justify-center mt-5',
            onSave: async () => {
                if (!orderId) {
                    toastify('error', '注文情報がありません');
                    modalStore.hideModal('repayment-confirm');
                    return;
                }

                try {
                    // Get payment URL from API for re-payment
                    const response = await flowResult(orderStore.rePayment(orderId));

                    if (response) {
                        // Redirect to GMO payment gateway
                        // GMO will redirect back to ROUTES.paymentResult.href after payment
                        localStorage.setItem('order_code', response.orderCode);
                        modalStore.hideModal('repayment-confirm');
                        window.location.href = response.paymentUrl;
                    }
                } catch (error: any) {
                    console.error('Re-payment error:', error);
                    toastify('error', error?.message || error?.response?.data?.message || '再支払い処理に失敗しました');
                    modalStore.hideModal('repayment-confirm');
                }
            }
        });
    }, [orderStore, modalStore]);

    return handleRepayment;
};

const ConfimRepaymentContentModal = () => {
    return (
        <section className='space-y-4'>
            <div className='size-20 p-2 bg-green-200 rounded-full flex justify-center items-center mx-auto text-primary'>
                <CreditCard className='size-10' />
            </div>

            <Heading variant='h5'>
                {/* Japan  */}
                再支払確認
            </Heading>

            <p className='text-text-secondary text-sm'>この注文を再支払しますか？</p>
        </section>
    );
};
